// src/modules/clinical-notes-v2/pages/AiClinicalNotePage.jsx
//
// Entry point del módulo Clinical Notes v2 (AI-assisted).
//
// Estructura visual:
//   1. Header con gradiente + avatar del paciente + acciones globales.
//   2. Action bar: template selector + chief complaint + doctor notes.
//   3. Patient context section (siempre visible): Care Gaps grid +
//      Coding Opportunities grid. Cada card con status chip, AI suggest
//      button y borde lateral coloreado por status.
//   4. Draft Note Editor (debajo, full width): TODOS los fields libres.
//      Las sugerencias IA aparecen como AiSuggestionPanel debajo de las
//      secciones de texto — el doctor decide si las usa.
//   5. Sticky save bar.
//   6. Dialog opcional "Clinical data" (meds/labs/allergies/chronic).

import * as React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import Grid from "@mui/material/Grid";

import AutoAwesomeIcon         from "@mui/icons-material/AutoAwesome";
import SaveIcon                from "@mui/icons-material/Save";
import ArrowBackIcon           from "@mui/icons-material/ArrowBack";
import RefreshIcon             from "@mui/icons-material/Refresh";
import PersonIcon              from "@mui/icons-material/Person";
import GppGoodIcon             from "@mui/icons-material/GppGood";
import LocalOfferIcon          from "@mui/icons-material/LocalOffer";
import FactCheckIcon           from "@mui/icons-material/FactCheck";

import CareGapSuggestionCard   from "../components/CareGapSuggestionCard";
import CodingOpSuggestionCard  from "../components/CodingOpSuggestionCard";
import DraftNoteEditor         from "../components/DraftNoteEditor";
import TemplateSelector, { TEMPLATES } from "../components/TemplateSelector";
import ClinicalDataDialog      from "../components/ClinicalDataDialog";

import useClinicalContextBundle from "../hooks/useClinicalContextBundle";
import {
  draftNote as apiDraftNote,
  suggestCareGap as apiSuggestCareGap,
  suggestCodingOpportunity as apiSuggestCodingOp,
  linkNote as apiLinkNote,
  stripAiTrace,
  toAdvmdTemplateName,
  RateLimitError,
} from "../services/clinicalNotesAiService";

import buildEhrNotePayload from "../../../utils/services/APP/ClinicalNotes/buildEhrNotePayload";
import { addEhrNote } from "../../../utils/services/AdvancedMD/ehrNotes";

// =============================================================
// Helpers
// =============================================================

const toUsDatetime = (date = new Date()) => {
  const d = new Date(date);
  const pad = (n) => (n < 10 ? `0${n}` : n);
  const h = d.getHours();
  const hour12 = ((h + 11) % 12) + 1;
  const ampm = h >= 12 ? "PM" : "AM";
  return `${pad(d.getMonth() + 1)}/${pad(d.getDate())}/${d.getFullYear()} ${pad(hour12)}:${pad(d.getMinutes())}:00 ${ampm}`;
};

// =============================================================
// Page
// =============================================================

export default function AiClinicalNotePage() {
  const nav = useNavigate();
  const { state } = useLocation();
  const isSmall = useMediaQuery((t) => t.breakpoints.down("md"));

  const patientId = state?.patientId || "";
  const cipId     = state?.cipId     || state?.patient?.cipId || "";
  const patient   = state?.patient || null;

  const [templateName, setTemplateName] = React.useState(TEMPLATES[2].advmdName); // default Annual Wellness
  const [chiefComplaint, setChiefComplaint] = React.useState("");
  const [freeFormNotes, setFreeFormNotes]   = React.useState("");

  const [selectedCareGapIds, setSelectedCareGapIds] = React.useState([]);
  const [selectedCodingOpIds, setSelectedCodingOpIds] = React.useState([]);

  const [careGapSuggestions, setCareGapSuggestions]   = React.useState({});
  const [codingOpSuggestions, setCodingOpSuggestions] = React.useState({});

  const [draft, setDraft] = React.useState(null);
  const [isAiSuggested, setIsAiSuggested] = React.useState(false);
  const [editedPaths] = React.useState(() => new Set());
  const [, forceRerender] = React.useState(0);

  const [generating, setGenerating] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [snack, setSnack] = React.useState({ open: false, severity: "info", message: "" });
  const [dataDialogOpen, setDataDialogOpen] = React.useState(false);

  // AI assistance toggle — default ON. Cuando OFF: clean form, no AI
  // buttons en cards, no AiSuggestionPanel en editor, no "Generate AI Draft".
  // Útil para pacientes sin gaps/ops o cuando el doctor quiere escribir manual.
  const [aiAssistEnabled, setAiAssistEnabled] = React.useState(true);

  const { bundle, isLoading: bundleLoading, error: bundleError, refetch } = useClinicalContextBundle({
    cipId,
    advancedMdPatientId: patientId,
    demographics: {
      age:        patient?.age,
      gender:     patient?.gender,
      memberType: patient?.memberType,
    },
    enabled: !!cipId,
  });

  // ----- handlers utilitarios -----

  const showSnack = (severity, message) => setSnack({ open: true, severity, message });

  const handleApiError = React.useCallback((e, fallback = "Unexpected error") => {
    if (e instanceof RateLimitError) {
      showSnack("warning", e.message);
      return;
    }
    const msg = e?.response?.data?.message || e?.message || fallback;
    showSnack("error", msg);
  }, []);

  const toggleId = (current, id, on) =>
    on ? Array.from(new Set([...current, id])) : current.filter((x) => x !== id);

  // ----- sugerencias individuales -----

  const requestCareGapSuggestion = async (gap) => {
    setCareGapSuggestions((s) => ({ ...s, [gap.careGapId]: { status: "loading" } }));
    try {
      const data = await apiSuggestCareGap({
        cipId,
        careGapId: gap.careGapId,
        templateName,
        extraContext: freeFormNotes || undefined,
        clinicalContext: bundle,
        gap,
      });
      setCareGapSuggestions((s) => ({ ...s, [gap.careGapId]: { status: "ready", data } }));
      showSnack("success", `Suggestion ready for ${gap.measure || gap.careGapId}.`);
    } catch (e) {
      setCareGapSuggestions((s) => ({
        ...s,
        [gap.careGapId]: { status: "error", error: e?.message || "Failed" },
      }));
      handleApiError(e, "Could not generate care gap suggestion.");
    }
  };

  const requestCodingOpSuggestion = async (op) => {
    setCodingOpSuggestions((s) => ({ ...s, [op.codingOpportunityId]: { status: "loading" } }));
    try {
      const data = await apiSuggestCodingOp({
        cipId,
        codingOpportunityId: op.codingOpportunityId,
        templateName,
        extraContext: freeFormNotes || undefined,
        clinicalContext: bundle,
        op,
      });
      setCodingOpSuggestions((s) => ({ ...s, [op.codingOpportunityId]: { status: "ready", data } }));
      showSnack("success", `Suggestion ready for ${op.suspectedIcd10 || op.codingOpportunityId}.`);
    } catch (e) {
      setCodingOpSuggestions((s) => ({
        ...s,
        [op.codingOpportunityId]: { status: "error", error: e?.message || "Failed" },
      }));
      handleApiError(e, "Could not generate coding opportunity suggestion.");
    }
  };

  // ----- apply plan to draft (botón "Apply to draft" en la card expandida) -----

  const appendText = (prev, addition) => {
    if (!addition?.trim()) return prev || "";
    if (!prev?.trim()) return addition;
    return `${prev.trim()}\n\n${addition}`;
  };

  const joinPlanList = (plan) =>
    Array.isArray(plan) && plan.length > 0 ? plan.map((p) => `• ${p}`).join("\n") : "";

  const ensureDraftSync = () => draft || emptyDraftForTemplate(templateName, patientId);

  const applyCareGapPlanToDraft = (gap, payload) => {
    const sug = payload?.suggestion;
    if (!sug) return;
    setDraft((prev) => {
      const base = prev || emptyDraftForTemplate(templateName, patientId);
      return {
        ...base,
        hpi: { ...base.hpi, general: appendText(base.hpi?.general, sug.hpi) },
        assessPlanFu: {
          ...base.assessPlanFu,
          assessments: appendText(base.assessPlanFu?.assessments, sug.assessment),
          treatment:   appendText(base.assessPlanFu?.treatment, joinPlanList(sug.plan)),
          followUp:    appendText(base.assessPlanFu?.followUp, sug.followUp),
        },
      };
    });
    setIsAiSuggested(true);
    ["hpi.general","assessPlanFu.assessments","assessPlanFu.treatment","assessPlanFu.followUp"]
      .forEach((p) => editedPaths.add(p));
    forceRerender((n) => n + 1);
    showSnack("success", `Plan applied to draft for ${gap.measure || gap.careGapId}.`);
    void ensureDraftSync;
  };

  const applyCodingOpPlanToDraft = (op, payload) => {
    const sug = payload?.suggestion;
    if (!sug) return;
    const examsText = (sug.examsRecommended || [])
      .map((e) => `• ${e.name}${e.rationale ? ` — ${e.rationale}` : ""}`).join("\n");
    const meatText = sug.meatDocumentation
      ? `M.E.A.T.\nMonitor: ${sug.meatDocumentation.monitor || "—"}\n` +
        `Evaluate: ${sug.meatDocumentation.evaluate || "—"}\n` +
        `Assess: ${sug.meatDocumentation.assess || "—"}\n` +
        `Treat: ${sug.meatDocumentation.treat || "—"}`
      : "";
    setDraft((prev) => {
      const base = prev || emptyDraftForTemplate(templateName, patientId);
      return {
        ...base,
        hpi: { ...base.hpi, general: appendText(base.hpi?.general, sug.hpiAddition) },
        assessPlanFu: {
          ...base.assessPlanFu,
          assessments: appendText(base.assessPlanFu?.assessments, sug.assessment),
          treatment:   appendText(
            base.assessPlanFu?.treatment,
            [examsText, meatText, joinPlanList(sug.plan)].filter(Boolean).join("\n\n")
          ),
        },
      };
    });
    setIsAiSuggested(true);
    ["hpi.general","assessPlanFu.assessments","assessPlanFu.treatment"]
      .forEach((p) => editedPaths.add(p));
    forceRerender((n) => n + 1);
    showSnack("success", `Plan applied for ${op.suspectedIcd10 || op.codingOpportunityId}.`);
  };

  // ----- generate full draft -----

  const generateDraft = async () => {
    if (!cipId)     { showSnack("warning", "Missing CIP."); return; }
    if (!patientId) { showSnack("warning", "Missing AdvancedMD patientId."); return; }

    setGenerating(true);
    try {
      const data = await apiDraftNote({
        cipId,
        advancedMdPatientId: patientId,
        templateName,
        selectedCareGapIds,
        selectedCodingOpportunityIds: selectedCodingOpIds,
        doctorContext: {
          chiefComplaint: chiefComplaint || undefined,
          freeFormNotes:  freeFormNotes  || undefined,
        },
        carryFromLatest: { vitals: true, medications: true, pmh: true },
        clinicalContext: bundle,
      });
      const clean = stripAiTrace(data);
      const meta = { ...(clean.meta || {}), templateName: toAdvmdTemplateName(clean.meta?.templateName) };
      const next = { ...clean, meta };
      setDraft(next);
      setIsAiSuggested(true);
      editedPaths.clear();
      forceRerender((n) => n + 1);
      showSnack("success", "AI draft generated. Review and edit before saving.");
    } catch (e) {
      handleApiError(e, "Could not generate draft.");
    } finally {
      setGenerating(false);
    }
  };

  // ----- start empty draft on demand -----

  const ensureDraft = () => {
    if (draft) return;
    setDraft(emptyDraftForTemplate(templateName, patientId));
  };

  // ----- save flow -----

  const validate = () => {
    if (!draft) return "Start the draft first (either type in any section or click 'Generate AI Draft').";
    if (!cipId) return "Missing patient CIP.";
    if (!patientId) return "Missing AdvancedMD patientId.";
    if (!draft.reason?.reasonForAppointment?.trim()) return "Reason for appointment is required.";
    const hasHpi = !!draft.hpi?.general?.trim();
    const hasAssess = !!draft.assessPlanFu?.assessments?.trim();
    if (!hasHpi && !hasAssess) return "Add at least HPI or an Assessment before saving.";
    return null;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) { showSnack("warning", err); return; }

    setSaving(true);
    const nowStr = toUsDatetime();

    const payloadInput = {
      ...draft,
      patient: { patientId, profileId: draft.patient?.profileId || "3" },
      meta: { ...(draft.meta || {}), msgtime: nowStr, noteDatetime: nowStr },
    };
    const xmlPayload = buildEhrNotePayload(payloadInput);

    try {
      const result = await addEhrNote(xmlPayload);
      const noteId = result?.id;
      const idTxt = noteId ? ` (ID: ${noteId})` : "";

      if (noteId) {
        apiLinkNote({
          cipId,
          advancedMdNoteId: String(noteId),
          templateName,
          linkedCareGaps: selectedCareGapIds.map((id) => ({
            careGapId: id,
            aiSuggestionAccepted: !!careGapSuggestions[id]?.data,
            aiSuggestionEditedByDoctor: editedPaths.size > 0,
          })),
          linkedCodingOpportunities: selectedCodingOpIds.map((id) => ({
            codingOpportunityId: id,
            aiSuggestionAccepted: !!codingOpSuggestions[id]?.data,
          })),
        }).catch(() => { /* endpoint hoy solo valida */ });
      }

      showSnack("success", `Clinical note created${idTxt}.`);
      setTimeout(() => nav(-1), 1200);
    } catch (e) {
      handleApiError(e, "Could not save the note.");
    } finally {
      setSaving(false);
    }
  };

  // ----- editor onChange tracker -----

  const handleDraftChange = (updater, path) => {
    setDraft((prev) => {
      const next = typeof updater === "function" ? updater(prev || emptyDraftForTemplate(templateName, patientId)) : updater;
      return next;
    });
    if (path) {
      editedPaths.add(path);
      forceRerender((n) => n + 1);
    }
  };

  // ----- guards -----

  if (!cipId || !patientId) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
          <Typography variant="h6" fontWeight={800}>Missing patient context</Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            This page must be opened from a Patient Map context (AI Clinical Note V2).
          </Typography>
          <Button sx={{ mt: 2 }} variant="contained" onClick={() => nav(-1)}>Go back</Button>
        </Paper>
      </Container>
    );
  }

  const patientName =
    patient?.fullName || patient?.name || patient?.patientName || bundle?.patient?.fullName || "—";
  const initials = String(patientName).split(" ").filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("");
  const age    = bundle?.patient?.age    ?? patient?.age;
  const gender = bundle?.patient?.gender ?? patient?.gender;

  const gaps = bundle?.openCareGaps || [];
  const ops  = bundle?.openCodingOpportunities || [];

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      {/* ============== HEADER ============== */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          mb: 2,
          background: "linear-gradient(135deg, #1565c0 0%, #1976d2 50%, #42a5f5 100%)",
          color: "common.white",
          overflow: "hidden",
        }}
      >
        <Stack direction={{ xs: "column", md: "row" }} alignItems={{ md: "center" }} spacing={2} sx={{ p: { xs: 2, md: 2.5 } }}>
          <IconButton onClick={() => nav(-1)} disabled={saving} sx={{ color: "common.white", bgcolor: "rgba(255,255,255,0.15)", "&:hover": { bgcolor: "rgba(255,255,255,0.25)" } }}>
            <ArrowBackIcon />
          </IconButton>

          <Avatar sx={{ width: 56, height: 56, bgcolor: "rgba(255,255,255,0.25)", color: "common.white", fontWeight: 800 }}>
            {initials || <PersonIcon />}
          </Avatar>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <AutoAwesomeIcon />
              <Typography variant="overline" sx={{ letterSpacing: 1, opacity: 0.85 }}>
                AI Clinical Note · V2
              </Typography>
            </Stack>
            <Typography variant="h5" fontWeight={900} sx={{ lineHeight: 1.1 }} noWrap>
              {patientName}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: "wrap", rowGap: 0.5 }}>
              <Chip size="small" label={`CIP ${cipId}`} sx={chipOnGradient} />
              <Chip size="small" label={`AdvMD ${patientId}`} sx={chipOnGradient} />
              {age    != null && <Chip size="small" label={`${age}y`} sx={chipOnGradient} />}
              {gender         && <Chip size="small" label={gender} sx={chipOnGradient} />}
            </Stack>
          </Box>

          <Stack direction="row" spacing={1}>
            <Tooltip title="Open clinical data (meds, labs, allergies, chronic)">
              <span>
                <Button
                  variant="contained"
                  startIcon={<FactCheckIcon />}
                  onClick={() => setDataDialogOpen(true)}
                  sx={{
                    bgcolor: "rgba(255,255,255,0.18)",
                    color: "common.white",
                    backdropFilter: "blur(4px)",
                    fontWeight: 700,
                    "&:hover": { bgcolor: "rgba(255,255,255,0.28)" },
                  }}
                >
                  Clinical data
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Reload patient context">
              <span>
                <IconButton
                  onClick={refetch}
                  disabled={bundleLoading || saving}
                  sx={{ color: "common.white", bgcolor: "rgba(255,255,255,0.15)", "&:hover": { bgcolor: "rgba(255,255,255,0.25)" } }}
                >
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
      </Paper>

      {/* ============== TEMPLATE + CONTEXT INPUTS + AI TOGGLE ============== */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Stack spacing={1.5}>
          <TemplateSelector value={templateName} onChange={setTemplateName} disabled={generating || saving} />

          {aiAssistEnabled && (
            <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
              <TextField
                fullWidth size="small"
                label="Chief complaint (optional)"
                value={chiefComplaint}
                onChange={(e) => setChiefComplaint(e.target.value)}
                disabled={generating || saving}
              />
              <TextField
                fullWidth size="small"
                label="Doctor notes for the AI (optional context, not part of the final note)"
                value={freeFormNotes}
                onChange={(e) => setFreeFormNotes(e.target.value)}
                disabled={generating || saving}
              />
            </Stack>
          )}

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "center" }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={aiAssistEnabled}
                  onChange={(e) => setAiAssistEnabled(e.target.checked)}
                  color="primary"
                  disabled={generating || saving}
                />
              }
              label={
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <AutoAwesomeIcon
                    fontSize="small"
                    color={aiAssistEnabled ? "primary" : "disabled"}
                  />
                  <Typography variant="body2" fontWeight={700}>
                    AI assistance {aiAssistEnabled ? "ON" : "OFF"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {aiAssistEnabled
                      ? "— click 'Plan of action' on a gap/op to get suggestions"
                      : "— write the note manually"}
                  </Typography>
                </Stack>
              }
            />

            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button variant="outlined" onClick={ensureDraft} disabled={generating || saving}>
                Start blank draft
              </Button>
              {aiAssistEnabled && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={generating ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
                  onClick={generateDraft}
                  disabled={generating || saving || bundleLoading}
                >
                  {draft ? "Regenerate AI Draft" : "Generate AI Draft"}
                </Button>
              )}
            </Stack>
          </Stack>
        </Stack>
      </Paper>

      {bundleError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load patient context: {bundleError?.message || "unknown error"}
        </Alert>
      )}

      {/* ============== CARE GAPS + CODING OPS GRID ============== */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: "100%" }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
              <Avatar sx={{ width: 30, height: 30, bgcolor: "error.50", color: "error.main" }}>
                <GppGoodIcon fontSize="small" />
              </Avatar>
              <Typography variant="subtitle1" fontWeight={800} sx={{ flex: 1 }}>Care Gaps</Typography>
              <Chip size="small" color="error" variant="outlined" label={`${gaps.length} open`} sx={{ fontWeight: 700 }} />
            </Stack>
            <Divider sx={{ mb: 1.5 }} />
            {bundleLoading ? (
              <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress size={20} /></Stack>
            ) : gaps.length === 0 ? (
              <Alert severity="success" variant="outlined" sx={{ borderStyle: "dashed" }}>
                No open care gaps for this patient.
              </Alert>
            ) : (
              <Stack spacing={1.25}>
                {gaps.map((gap) => (
                  <CareGapSuggestionCard
                    key={gap.careGapId}
                    gap={gap}
                    selected={selectedCareGapIds.includes(gap.careGapId)}
                    onToggleSelected={(id, on) => setSelectedCareGapIds((c) => toggleId(c, id, on))}
                    suggestionState={careGapSuggestions[gap.careGapId]}
                    onRequestSuggestion={requestCareGapSuggestion}
                    onApplyPlanToDraft={applyCareGapPlanToDraft}
                    aiEnabled={aiAssistEnabled}
                    disabled={generating || saving}
                  />
                ))}
              </Stack>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: "100%" }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
              <Avatar sx={{ width: 30, height: 30, bgcolor: "secondary.50", color: "secondary.main" }}>
                <LocalOfferIcon fontSize="small" />
              </Avatar>
              <Typography variant="subtitle1" fontWeight={800} sx={{ flex: 1 }}>Coding Opportunities</Typography>
              <Chip size="small" color="secondary" variant="outlined" label={`${ops.length} open`} sx={{ fontWeight: 700 }} />
            </Stack>
            <Divider sx={{ mb: 1.5 }} />
            {bundleLoading ? (
              <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress size={20} /></Stack>
            ) : ops.length === 0 ? (
              <Alert severity="info" variant="outlined" sx={{ borderStyle: "dashed" }}>
                No open coding opportunities for this patient.
              </Alert>
            ) : (
              <Stack spacing={1.25}>
                {ops.map((op) => (
                  <CodingOpSuggestionCard
                    key={op.codingOpportunityId}
                    op={op}
                    selected={selectedCodingOpIds.includes(op.codingOpportunityId)}
                    onToggleSelected={(id, on) => setSelectedCodingOpIds((c) => toggleId(c, id, on))}
                    suggestionState={codingOpSuggestions[op.codingOpportunityId]}
                    onRequestSuggestion={requestCodingOpSuggestion}
                    onApplyPlanToDraft={applyCodingOpPlanToDraft}
                    aiEnabled={aiAssistEnabled}
                    disabled={generating || saving}
                  />
                ))}
              </Stack>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* ============== DRAFT EDITOR ============== */}
      <Box sx={{ mb: 2 }}>
        <DraftNoteEditor
          draft={draft}
          onChange={handleDraftChange}
          editedPaths={editedPaths}
          gaps={aiAssistEnabled ? gaps : []}
          ops={aiAssistEnabled ? ops  : []}
          careGapSuggestions={aiAssistEnabled ? careGapSuggestions   : {}}
          codingOpSuggestions={aiAssistEnabled ? codingOpSuggestions : {}}
        />
      </Box>

      {/* ============== STICKY SAVE BAR ============== */}
      <Paper
        elevation={6}
        sx={{
          position: "sticky",
          bottom: { xs: 0, md: 16 },
          mt: 2,
          p: 1.5,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          background: "linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(255,255,255,1) 100%)",
          backdropFilter: "blur(4px)",
          zIndex: 5,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
          spacing={1}
        >
          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
            <Chip size="small" label={templateName} sx={{ fontWeight: 700 }} />
            {draft ? (
              <Chip
                size="small"
                color={isAiSuggested ? "primary" : "default"}
                variant="outlined"
                label={isAiSuggested ? "AI-assisted" : "Manual"}
                sx={{ fontWeight: 700 }}
              />
            ) : (
              <Chip size="small" variant="outlined" label="No draft yet" />
            )}
            {editedPaths.size > 0 && (
              <Chip size="small" color="warning" variant="outlined" label={`${editedPaths.size} field${editedPaths.size === 1 ? "" : "s"} edited`} />
            )}
            {selectedCareGapIds.length + selectedCodingOpIds.length > 0 && (
              <Chip
                size="small"
                color="info"
                variant="outlined"
                label={`${selectedCareGapIds.length} gap${selectedCareGapIds.length === 1 ? "" : "s"} · ${selectedCodingOpIds.length} coding op${selectedCodingOpIds.length === 1 ? "" : "s"}`}
              />
            )}
          </Stack>
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button variant="outlined" onClick={() => nav(-1)} disabled={saving} size={isSmall ? "small" : "medium"}>
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              onClick={handleSave}
              disabled={!draft || saving}
              size={isSmall ? "small" : "medium"}
            >
              {saving ? "Saving…" : "Save note"}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* ============== CLINICAL DATA DIALOG ============== */}
      <ClinicalDataDialog
        open={dataDialogOpen}
        onClose={() => setDataDialogOpen(false)}
        bundle={bundle}
      />

      {/* ============== SNACKBAR ============== */}
      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={snack.severity}
          variant="filled"
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// =============================================================
// Estilos compartidos
// =============================================================

const chipOnGradient = {
  bgcolor: "rgba(255,255,255,0.18)",
  color: "common.white",
  fontWeight: 700,
  border: "1px solid rgba(255,255,255,0.25)",
};

// =============================================================
// Helpers locales
// =============================================================

function emptyDraftForTemplate(templateName, patientId) {
  return {
    patient: { patientId, profileId: "3" },
    meta: { templateName, comment: templateName, noteDatetime: toUsDatetime(), msgtime: toUsDatetime(), nocookie: "0" },
    location: {},
    reason: { reasonForAppointment: TEMPLATES.find((t) => t.advmdName === templateName)?.reasonDefault || "" },
    vitals: {},
    hpi: { general: "", tcm: "" },
    meds: { allReviewedAndReconciled: false, text: "" },
    pmh: { noChangeReviewed: false, text: "" },
    ros: {},
    exam: {},
    phq2: { q1: "", q2: "", reviewedPhq9: "" },
    assessPlanFu: { assessments: "", treatment: "", followUp: "" },
  };
}
